/**
 * Learner Diagnostic Profile page.
 *
 * Shows, per skill module, macro-competency bars and the top-3 fine-grained
 * weaknesses with a confidence badge and a recommended resource once the
 * configured cold-start gate is met. Below the gate a module shows a "keep
 * practising" state. Gated server-side by DIAGNOSTIC_PROFILE_ENABLED.
 *
 * @module DiagnosticProfilePage
 */

import { Info, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { useDiagnosticProfile } from "@/hooks/useDiagnosticProfile";
import {
  type DiagnosticWeakness,
  type MacroCompetency,
  type ModuleProfile,
} from "@/lib/diagnosticApi";

const MODULE_LABELS: Record<string, string> = {
  reading: "Lesen",
  listening: "Hören",
  writing: "Schreiben",
  speaking: "Sprechen",
};

const CONFIDENCE_VARIANT: Record<string, "default" | "secondary" | "outline"> =
  {
    high: "default",
    medium: "secondary",
    low: "outline",
  };

function MacroBar({ macro }: { readonly macro: MacroCompetency }) {
  const { t } = useTranslation();
  const pct = Math.min(100, Math.round(macro.rate * 100));
  const label = t(`diagnostic.macros.${macro.macro}.label`, {
    defaultValue: macro.macro,
  });
  const description = t(`diagnostic.macros.${macro.macro}.description`, {
    defaultValue: "",
  });

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="font-ui text-xs font-medium text-foreground">
          {label}
        </span>
        <span className="font-ui text-[11px] text-muted-foreground">
          {pct}%
        </span>
      </div>
      {description && (
        <p className="font-ui text-[11px] leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary/70"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function WeaknessCard({ weakness }: { readonly weakness: DiagnosticWeakness }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border/60 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
          {weakness.tag}
        </code>
        <Badge variant={CONFIDENCE_VARIANT[weakness.confidence] ?? "outline"}>
          {t(`diagnostic.confidence.${weakness.confidence}`, {
            defaultValue: weakness.confidence,
          })}
        </Badge>
      </div>
      <p className="font-ui text-sm text-foreground">{weakness.explain}</p>
      {weakness.resource?.title ? (
        <p className="font-ui mt-2 text-xs text-muted-foreground">
          {t("diagnostic.profile.recommended", { defaultValue: "Recommended" })}
          :{" "}
          <span className="font-medium text-foreground">
            {weakness.resource.title}
          </span>
        </p>
      ) : null}
    </div>
  );
}

function ModuleSection({
  module,
  requiredItems,
}: {
  readonly module: ModuleProfile;
  readonly requiredItems: number;
}) {
  const { t } = useTranslation();
  const label = MODULE_LABELS[module.module] ?? module.module;
  return (
    <Card>
      <CardHeader className="flex flex-col gap-1 space-y-0 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <CardTitle className="font-ui text-base">{label}</CardTitle>
        <span className="font-ui text-xs text-muted-foreground">
          {t("diagnostic.profile.attempted", {
            defaultValue: "{{count}} items attempted",
            count: module.attempted_items,
          })}
        </span>
      </CardHeader>
      <CardContent className="space-y-5 p-4 pt-0 sm:p-6 sm:pt-0">
        {!module.unlocked ? (
          <p className="font-ui rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
            {t("diagnostic.profile.coldStart", {
              defaultValue:
                "Keep practising — your detailed profile unlocks at {{count}} attempted items.",
              count: requiredItems,
            })}
          </p>
        ) : module.macros.length > 0 ? (
          <>
            <div className="space-y-3">
              {module.macros.map((m) => (
                <MacroBar key={m.macro} macro={m} />
              ))}
            </div>
            {module.weaknesses.length > 0 ? (
              <div className="space-y-3">
                <p className="font-ui text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("diagnostic.profile.topWeaknesses", {
                    defaultValue: "Top focus areas",
                  })}
                </p>
                {module.weaknesses.map((w) => (
                  <WeaknessCard key={`${w.module}-${w.tag}`} weakness={w} />
                ))}
              </div>
            ) : (
              <p className="font-ui text-sm text-muted-foreground">
                {t("diagnostic.profile.noWeaknesses", {
                  defaultValue: "No standout weaknesses — nice work!",
                })}
              </p>
            )}
          </>
        ) : (
          <p className="font-ui text-sm text-muted-foreground">
            {t("diagnostic.profile.noData", {
              defaultValue: "No data yet for this skill.",
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function DiagnosticProfilePage() {
  const { t } = useTranslation();
  const { profile, isLoading } = useDiagnosticProfile();

  return (
    <StandardPageShell
      title={t("diagnostic.profile.title", {
        defaultValue: "Diagnostic Profile",
      })}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
    >
      <p className="font-ui text-sm text-muted-foreground">
        {t("diagnostic.profile.subtitle", {
          defaultValue:
            "Where your errors cluster across the four skills, and what to practise.",
        })}
      </p>
      <div className="mb-6 mt-3 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="font-ui text-xs leading-relaxed text-muted-foreground">
          {t("diagnostic.profile.rateExplanation", {
            defaultValue:
              "The percentage shows how often this category caused difficulty. Lower is better.",
          })}
        </p>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Loader2
            className="h-8 w-8 animate-spin text-primary/40"
            aria-hidden
          />
        </div>
      ) : !profile?.enabled ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <p className="font-ui text-sm text-muted-foreground">
              {t("diagnostic.profile.disabled", {
                defaultValue: "The diagnostic profile is not available yet.",
              })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {profile.modules.map((m) => (
            <ModuleSection
              key={m.module}
              module={m}
              requiredItems={profile.required_items}
            />
          ))}
        </div>
      )}
    </StandardPageShell>
  );
}
